if (!customElements.get('product-bundle')) {
  class ProductBundle extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        product: '.product-bundle__item',
        productMasterSelect: '.js-master-select select',
        productOption: '.js-option select',
        productPrice: '.product-bundle__item-price',
        totalDiscount: '.js-total-discount',
        totalPrice: '.js-total-bundle-price',
        form: 'form',
        submit: 'form [type="submit"]',
      };
      this.submitButton = this.querySelector(this.selectors.submit);
      this.cartDrawer = document.querySelector('cart-drawer');
    }

    connectedCallback() {
      const checkProductsIsAvailable = this.checkProductsIsAvailable();

      if (checkProductsIsAvailable) {
        this.setTotalDiscounts();
        this.setTotalBundlePrice();
      }

      for (const select of this.querySelectorAll('.js-master-select select')) {
        select.addEventListener('change', (event) => {
          const checkProductsIsAvailable = this.checkProductsIsAvailable();

          if (checkProductsIsAvailable) {
            this.setProductsPrice();
            this.setTotalDiscounts();
            this.setTotalBundlePrice();
          }
        });
      }

      for (const select of this.querySelectorAll('.js-option select')) {
        select.addEventListener('change', (event) => {
          const product = select.closest(this.selectors.product);
          const masterSelect = product.querySelector(this.selectors.productMasterSelect);
          const options = product.querySelectorAll(this.selectors.productOption);
          let optionSelector = '';

          options.forEach(option => {
            optionSelector += `[data-option-${option.dataset.optionPosition}="${option.value}"]`;
          });

          if (masterSelect.querySelector(optionSelector)) {
            masterSelect.value = masterSelect.querySelector(optionSelector).value;

            masterSelect.dispatchEvent(new CustomEvent('change'));
          }
        });
      }

      // Sync PDP variant selection → bundle dropdowns
      // When user selects a size on the main PDP, auto-select the same
      // size in the bundle's first product (same product) and any other
      // bundle products that share the same option values.
      this.#syncFromPDP();

      this.querySelector(this.selectors.form)?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.handleSubmit();
      });
    }

    /**
     * Listen for PDP variant changes (radio buttons + selects) and sync
     * matching option values into the bundle product dropdowns.
     */
    #syncFromPDP() {
      // Find the PDP product-selector (works for both buttons and dropdowns)
      const pdpSelector = document.querySelector('product-selector, set-product-selector');
      const pdpForm = document.querySelector('form[action="/cart/add"]:not(product-bundle form)');
      const container = pdpSelector || pdpForm;
      if (!container) return;

      // Listen for ALL input/select changes within the PDP selector
      container.addEventListener('change', (event) => {
        const target = event.target;

        // Radio button
        if (target.type === 'radio' && target.checked && target.dataset?.option) {
          this.#applyPDPOption(target.dataset.option, target.value);
          return;
        }

        // Select dropdown
        if (target.tagName === 'SELECT' && target.value) {
          let optionKey = null;
          // From selected option's data-option
          const selectedOpt = target.querySelector('option:checked[data-option]');
          if (selectedOpt) optionKey = selectedOpt.dataset.option;
          // From select ID: "Option-section-0" → "option1"
          if (!optionKey) {
            const idMatch = target.id?.match(/(\d+)$/);
            if (idMatch) optionKey = 'option' + (parseInt(idMatch[1]) + 1);
          }
          if (optionKey) {
            this.#applyPDPOption(optionKey, target.value);
          }
        }
      });

      // Apply already-selected PDP options on page load
      const radios = container.querySelectorAll('input[type="radio"]:checked[data-option]');
      for (const radio of radios) {
        this.#applyPDPOption(radio.dataset.option, radio.value);
      }
      const selects = container.querySelectorAll('select');
      for (const sel of selects) {
        if (sel.value) {
          const opt = sel.querySelector('option:checked[data-option]');
          if (opt) this.#applyPDPOption(opt.dataset.option, sel.value);
        }
      }
    }

    /**
     * Apply a PDP option value to all matching bundle product dropdowns.
     * @param {string} optionKey - e.g. "option1"
     * @param {string} value - e.g. "17 Pro Max"
     */
    #applyPDPOption(optionKey, value) {
      if (!optionKey || !value) return;
      const position = optionKey.replace('option', '');

      // Find all bundle product option selects with matching position
      const bundleSelects = this.querySelectorAll(
        `.js-option select[data-option-position="${position}"]`
      );

      for (const sel of bundleSelects) {
        const matchingOption = sel.querySelector(`option[value="${CSS.escape(value)}"]`);
        if (matchingOption && !matchingOption.disabled) {
          sel.value = value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    checkProductsIsAvailable() {
      let available = true;
      let noOptionSelected = false;

      this.querySelectorAll(this.selectors.product).forEach(productItem => {
        const masterSelect = productItem.querySelector(this.selectors.productMasterSelect);

        if (masterSelect) {
          const masterSelectValue = masterSelect.value;
          const optionIsDisable = masterSelect.querySelector(`option:checked`)?.hasAttribute('disabled');

          if (masterSelectValue === '') {
            available = false;
            noOptionSelected = true;
          } else if (masterSelectValue !== '' && optionIsDisable) {
            available = false;
          }
        }
      });

      if (noOptionSelected) {
        this.disableBundle(true);
      } else if (!available) {
        this.disableBundle();
      } else {
        this.enableBundle();
      }

      return available;
    }

    enableBundle() {
      this.querySelector(this.selectors.totalDiscount)?.classList.remove('hidden');
      this.querySelector(this.selectors.totalPrice)?.classList.remove('hidden');

      this.submitButton.classList.remove('disabled');

      this.submitButton.querySelector('.button-label').textContent = window.variantStrings.addToCart;
    }

    disableBundle(noOptionSelected = false) {
      this.querySelector(this.selectors.totalDiscount)?.classList.add('hidden');
      this.querySelector(this.selectors.totalPrice)?.classList.add('hidden');

      this.submitButton.classList.add('disabled');

      if (noOptionSelected) {
        this.submitButton.querySelector('.button-label').textContent = window.variantStrings.selectOption;
      } else {
        this.submitButton.querySelector('.button-label').textContent = window.variantStrings.soldOut;
      }
    }

    /**
     * Get a single product's price.
     *
     * @param {Node} product
     * @returns {Number}
     */
    getProductPrice(product) {
      if (!product) return 0;

      const select = product.querySelector('.js-master-select select');
      if (!!select) {
        return Number(select.querySelector('option:checked')?.dataset?.price) || 0;
      } else {
        return Number(product.querySelector('input[data-price]')?.dataset.price) || 0;
      }
    }

    /**
     * Get the total price of all bundle products.
     * @returns {Number}
     */
    getTotalBundlePrice() {
      const products = this.querySelectorAll(this.selectors.product);
      let bundlePrice = 0;

      products.forEach(product => {
        bundlePrice += this.getProductPrice(product);
      });

      return bundlePrice;
    }

    /**
     * Set total bundle price.
     */
    setTotalBundlePrice() {
      const target = this.querySelector(this.selectors.totalPrice);
      target &&
        (target.innerHTML = Shopify.formatMoney(this.getTotalBundlePrice(), Shopify.current_money_format));
    }

    /**
     * Set product price based on data string.
     */
    setProductsPrice() {
      const products = this.querySelectorAll(this.selectors.product);
      for (const product of products) {
        const select = product.querySelector('.js-master-select select');
        if (!!select) {
          const selectedOption =
            select.querySelector('option:checked');
          product.querySelector(
            this.selectors.productPrice
          ).innerHTML = selectedOption.dataset.priceString;
        }
      }
    }

    /**
     * Set total discounts from all discounted bundle products.
     */
    setTotalDiscounts() {
      const totalDiscount = this.querySelector(this.selectors.totalDiscount);
      const prices = this.querySelectorAll('[data-discount-value]:checked, input[data-discount-value]');
      let total = 0;

      prices.forEach(price => {
        total += Number(price.dataset.discountValue);
      });

      totalDiscount.innerHTML = Shopify.formatMoney(Number(total), Shopify.current_money_format);
    }

    /**
     * Handle bundle form submit.
     */
    handleSubmit() {
      const form = this.querySelector(this.selectors.form);
      const data = new FormData(form);
      const now  = new Date().getTime();
      this.submitButton.classList.add('disabled');

      for (const key of data.keys()) {
        key.includes('_bundle_stamp') && data.set(key, now);
      }

      // Set up request.
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      data.append(
        'sections',
        this.cartDrawer
          .getSectionsToRender()
          .map(section => section.section)
      );
      data.append('sections_url', window.location.pathname);
      config.body = data;

      fetch(`${routes.cart_add_url}`, config)
        .then(response => response.json())
        .then(response => {
          if (response.status) {
            return;
          }

          this.cartDrawer.renderContents(response);
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          this.submitButton.classList.remove('disabled');
        });
    }
  }
  customElements.define('product-bundle', ProductBundle);
}
